import { Uuid } from "spacetimedb";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { Button } from "@/web/components/primitives/button";
import { ReadOnlyMutationsProvider } from "@/web/lib/readOnlyMutations";
import { useProcedureQuery } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import {
  AdminSmbDataProvider,
  type AdminSmbData,
} from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { SmbDashboardContent } from "@/web/routes/_protected/profile/smb";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/_protected/admin/smb/$brandId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { brandId } = Route.useParams();

  // Validate before parsing: a malformed id in the URL shouldn't blow up the
  // suspense boundary — show a friendly not-found instead. A format-valid
  // string never makes `Uuid.parse` throw, so no catch is needed.
  const brandUuid = useMemo(() => (UUID_RE.test(brandId) ? Uuid.parse(brandId) : null), [brandId]);

  if (!brandUuid) {
    return <AdminSmbMessage title="Invalid brand id" detail={brandId} />;
  }

  return <AdminSmbViewer brandUuid={brandUuid} brandIdStr={brandId} />;
}

function AdminSmbViewer({ brandUuid, brandIdStr }: { brandUuid: Uuid; brandIdStr: string }) {
  const { data: snapshot, refetch } = useProcedureQuery(procedures.adminBrandSnapshot, {
    brandId: brandUuid,
  });

  const adminData = useMemo<AdminSmbData>(
    () => ({
      superView: snapshot.superView,
      cardEngineInputs: snapshot.cardEngineInputs,
      underwritableEntities: snapshot.underwritableEntities,
      brands: snapshot.brands,
      shareBusinessCardContexts: snapshot.shareBusinessCardContexts,
      targetableProperties: snapshot.targetableProperties,
      operatingProperties: snapshot.operatingProperties,
      operatingPropertiesJobStatus: snapshot.operatingPropertiesJobStatus,
    }),
    [snapshot],
  );

  if (snapshot.underwritableEntities.length === 0) {
    return (
      <AdminSmbMessage
        title="Nothing to show yet"
        detail={`Brand ${brandIdStr} has no underwritable entity — likely mid-onboarding.`}
      />
    );
  }

  const friendlyName = snapshot.superView[0]?.brand.friendlyName ?? brandIdStr;

  return (
    <ReadOnlyMutationsProvider>
      <AdminSmbDataProvider value={adminData}>
        <AdminViewerBanner
          friendlyName={friendlyName}
          brandIdStr={brandIdStr}
          onRefresh={() => void refetch()}
        />
        {/* Offset the reused dashboard below the fixed banner. */}
        <div className="pt-9">
          <SmbDashboardContent />
        </div>
      </AdminSmbDataProvider>
    </ReadOnlyMutationsProvider>
  );
}

function AdminViewerBanner({
  friendlyName,
  brandIdStr,
  onRefresh,
}: {
  friendlyName: string;
  brandIdStr: string;
  onRefresh: () => void;
}) {
  return (
    <div className="fixed inset-x-0 top-0 z-[100] flex h-9 items-center justify-between gap-4 bg-withus-yellow px-4 text-xs font-semibold text-withus-dark-blue">
      <span className="truncate">
        🔒 Admin read-only view — {friendlyName}{" "}
        <code className="font-mono opacity-60">({brandIdStr})</code>
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" onClick={onRefresh} className="underline underline-offset-2">
          Refresh
        </button>
        <Link to="/admin" className="underline underline-offset-2">
          ← Admin
        </Link>
      </div>
    </div>
  );
}

function AdminSmbMessage({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-lg font-semibold text-withus-dark-blue">{title}</h1>
      {detail ? <p className="max-w-md text-sm text-withus-dark-blue/60">{detail}</p> : null}
      <Button variant="outline" asChild>
        <Link to="/admin">← Back to admin</Link>
      </Button>
    </div>
  );
}
